import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';

/**
 * Serviço para gerenciar o upload e armazenamento de arquivos
 */
export class StorageService {
  /**
   * Faz upload de uma imagem para o Supabase Storage
   * 
   * @param file Arquivo de imagem
   * @param bucket Nome do bucket de armazenamento
   * @param path Caminho dentro do bucket (opcional)
   * @returns URL da imagem ou null em caso de erro
   */
  async uploadImage(
    file: File,
    bucket: string = 'avatars',
    path?: string
  ): Promise<{ url: string | null; error: string | null }> {
    try {
      // Verificar se é uma imagem
      if (!file.type.startsWith('image/')) {
        return {
          url: null,
          error: 'O arquivo precisa ser uma imagem'
        };
      }

      // Limitar tamanho a 2MB
      const MAX_SIZE = 2 * 1024 * 1024; // 2MB
      if (file.size > MAX_SIZE) {
        return {
          url: null,
          error: 'A imagem deve ter menos de 2MB'
        };
      }

      // Criar nome único para o arquivo
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = path ? `${path}/${fileName}` : fileName;

      // Fazer upload para o Supabase Storage
      const { data, error } = await supabase
        .storage
        .from(bucket)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Erro no upload de imagem:', error);
        return {
          url: null,
          error: 'Falha ao fazer upload da imagem'
        };
      }

      // Obter URL pública
      const { data: urlData } = supabase
        .storage
        .from(bucket)
        .getPublicUrl(data.path);

      return {
        url: urlData.publicUrl,
        error: null
      };
    } catch (error: any) {
      console.error('Erro inesperado no upload:', error);
      return {
        url: null,
        error: error.message || 'Erro desconhecido ao fazer upload'
      };
    }
  }

  /**
   * Exclui uma imagem do Supabase Storage
   * 
   * @param url URL completa da imagem
   * @param bucket Nome do bucket
   * @returns Sucesso ou falha na operação
   */
  async deleteImage(
    url: string,
    bucket: string = 'avatars'
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      // Extrair o caminho do arquivo da URL
      const urlObj = new URL(url);
      const pathSegments = urlObj.pathname.split('/');
      const fileName = pathSegments[pathSegments.length - 1];
      
      // Determinar o caminho dentro do bucket
      let filePath = '';
      
      // Verificar se a URL é do Supabase Storage
      if (url.includes('/storage/v1/object/public/')) {
        // Formato: URL_BASE/storage/v1/object/public/BUCKET/PATH/FILE
        const bucketIndex = pathSegments.findIndex(segment => segment === bucket);
        if (bucketIndex !== -1) {
          filePath = pathSegments.slice(bucketIndex + 1).join('/');
        }
      } else {
        // Fallback: usar só o nome do arquivo
        filePath = fileName;
      }
      
      if (!filePath) {
        return {
          success: false,
          error: 'Não foi possível determinar o caminho do arquivo'
        };
      }

      // Excluir do Storage
      const { error } = await supabase
        .storage
        .from(bucket)
        .remove([filePath]);

      if (error) {
        console.error('Erro ao excluir imagem:', error);
        return {
          success: false,
          error: 'Falha ao excluir a imagem'
        };
      }

      return {
        success: true,
        error: null
      };
    } catch (error: any) {
      console.error('Erro inesperado ao excluir imagem:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao excluir imagem'
      };
    }
  }

  /**
   * Exclui uma imagem do Supabase Storage usando o caminho do arquivo.
   * 
   * @param filePath Caminho completo do arquivo dentro do bucket
   * @param bucket Nome do bucket
   * @returns Sucesso ou falha na operação
   */
  async deleteImageByPath(
    filePath: string,
    bucket: string = 'avatars'
  ): Promise<{ success: boolean; error: string | null }> {
    if (!filePath) {
      console.error('deleteImageByPath: filePath não fornecido.');
      return {
        success: false,
        error: 'Caminho do arquivo não fornecido para exclusão.'
      };
    }
    try {
      const { error } = await supabase
        .storage
        .from(bucket)
        .remove([filePath]);

      if (error) {
        console.error('Erro ao excluir imagem por caminho:', error);
        return {
          success: false,
          error: 'Falha ao excluir a imagem por caminho.'
        };
      }
      return {
        success: true,
        error: null
      };
    } catch (error: any) {
      console.error('Erro inesperado ao excluir imagem por caminho:', error);
      return {
        success: false,
        error: error.message || 'Erro desconhecido ao excluir imagem por caminho.'
      };
    }
  }
}

export const storageService = new StorageService(); 